import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	OnDestroy,
	OnInit,
	computed,
	inject
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { AlertService } from '@app/core/services/alert.service';
import { MaterialModule } from '@app/material.module';
import { PoMaintenanceService } from '@app/purchase-orders/entry-inquiries/purchase-order/services/po-maintenance.service';
import { selectRolePermissionState } from '@app/store/role-permission';
import { AuthService } from '@core/services/auth.service';
import { ConstantsService } from '@core/services/constants.service';
import { HomeStateService } from '@core/services/home-state.service';
import { NotificationService } from '@core/services/notification.service';
import { RcService } from '@core/services/rc.service';
import { SubfolderService } from '@core/services/subfolder.service';
import { Store } from '@ngrx/store';
import { SettingsComponent } from '@pages/training/settings/settings.component';
import { TrainingService } from '@pages/training/training.service';
import { ChangePasswordComponent } from '@shared/change-password/change-password.component';
import { ItPasswordResetComponent } from '@shared/it-password-reset/it-password-reset.component';
import { ProfileComponent } from '@shared/profile/profile.component';
import { SharedService } from '@shared/shared.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription, filter } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Component({
	selector: 'app-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.css'],
	imports: [CommonModule, RouterModule, MaterialModule],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit, OnDestroy {
	tenant: any = {};
	tenantName: string = '';
	userData: any;
	/** Extra links from [TCK].[GetResources], rendered just above Logout. */
	resources: any[] = [];
	employeeNumber: number = 0;
	tenantLogo: string = '';
	isInventoryAccess: boolean = false;

	hasEvents: boolean = false;
	private eventsSubscription!: Subscription;
	private poSubscription: Subscription;
	private rcSubscription!: Subscription;

	private _sidebarOpen = false;
	private _submenuOpen = false;

	isSupervisor: boolean = false;
	isReviewer: boolean = false;
	IsTraining: boolean = false;
	IsTrainingAdmin: boolean = false;
	notificationsShown: boolean = false;
	MailSettings: boolean = false;
	TrainingCourse: boolean = false;
	CourseNotifications: boolean = false;
	TrainingGroup: boolean = false;
	EmployeeTraining: boolean = false;
	EmployeeNotifications: boolean = false;
	isUpdatePasswordAvailable: boolean = false;
	forcePasswordChange: boolean = false;
	isIT: boolean = false;
	forcePasswordReset: boolean = false;
	isItemMasterAccess: boolean = false;
	isPurchaseOrderAccess: boolean = false;
	isPTOIntegration: boolean = false;
	// Track inventory access check to prevent unnecessary API calls
	inventoryAccessChecked: boolean = false;
	lastCheckedEmployeeNumber: number = 0;
	private inventoryAccessInFlight = false;
	private sidebarAccessLoadedForEmployee: number | null = null;
	private poUpdateFlagLoadedForUser: string | null = null;
	private poUpdateFlagInFlight = false;

	notifications: any[] = [];
	NotificationPagingInfo: any = {
		Search: '',
		Searchcolumn: '',
		Page: 1,
		ItemsPerPage: 10,
		SortColumn: 'ValidityExpired',
		SortBy: 'ASC',
		reverse: false,
		EmployeeNumber: ''
	};
	trainingNotificationList: any[] = [];

	isDisabled: boolean = false; // Change this based on your condition
	UserName: any;
	private userSubscription!: Subscription;
	private notificationSubscription!: Subscription;
	private profileSubscription!: Subscription;

	private readonly store = inject(Store);
	public readonly rcService = inject(RcService);

	roleMenu$ = this.store.select(selectRolePermissionState);

	menu = toSignal(
		this.store.select(selectRolePermissionState).pipe(
			map((state) => {
				return state?.data || []; // Safely extract data with fallback to empty array
			})
		),
		{ initialValue: [] } // Start with empty array until store emits
	);

	permissions = computed(() => {
		const menuItems = this.menu();
		if (!menuItems || !Array.isArray(menuItems)) {
			return new Set();
		}

		const permissions = new Set(menuItems.map((m: any) => m.Object));
		return permissions;
	});

	icPermissions = computed(
		() =>
			new Set(
				this.menu()
					?.filter((m: any) => m.Module === 'IC')
					.map((m: any) => m.Object) ?? []
			)
	);

	poPermissions = computed(
		() =>
			new Set(
				this.menu()
					?.filter(
						(m: any) => m.Module === 'PO' || m.Module === 'tpPO'
					)
					.map((m: any) => m.Object) ?? []
			)
	);

	icReportEnable = computed(
		() =>
			this.menu()?.some(
				(m: any) => m.Object === 'IC - Reports' && m.Module === '0'
			) ?? false
	);

	poReportEnable = computed(
		() =>
			this.menu()?.some(
				(m: any) => m.Object === 'PO - Reports' && m.Module === '0'
			) ?? false
	);

	constructor(
		readonly el: ElementRef,
		private readonly router: Router,
		private readonly authService: AuthService,
		private readonly alertService: AlertService,
		private readonly poService: PoMaintenanceService,
		private readonly dialogRef: MatDialog,
		private readonly constantService: ConstantsService,
		private readonly sharedService: SharedService,
		private readonly trainingService: TrainingService,
		private readonly subfolderService: SubfolderService,
		private readonly notificationService: NotificationService,
		private readonly homeStateService: HomeStateService,
		private readonly toastr: ToastrService,
		private readonly cdr: ChangeDetectorRef
	) {
		// Initialize tenant name and logo
		this.initializeTenantInfo();

		this.poSubscription = this.sharedService.poUpdateEvent$.subscribe(
			(status) => {
				this.isDisabled = status;
			}
		);
	}

	private initializeTenantInfo(): void {
		const tenant = this.rcService.getTenant();
		if (this.subfolderService.subfolder !== '') {
			this.tenantName = this.subfolderService.subfolder;
		} else if (tenant) {
			// If a tenant is already set via rcService
			this.tenantName = tenant;
		} else {
			this.tenantName = ''; // Default case (root URL, no tenant)
		}

		// Get tenant logo URL (will use API data if available in production/beta, otherwise fallback to assets)
		this.tenantLogo = this.rcService.getTenantLogoUrl(
			this.constantService.DEFAULT_LOGO
		);
	}

	ngOnInit(): void {
		// Initialize tenant info on component init to ensure it's loaded from storage
		this.initializeTenantInfo();

		// Subscribe to tenant changes
		this.rcSubscription = this.rcService.currentRc$.subscribe((tenant) => {
			this.initializeTenantInfo();
			this.cdr.detectChanges();
		});

		// Subscribe to tenant data changes (for production/beta environments with API data)
		this.rcService.tenantData$.subscribe((tenantData) => {
			if (tenantData) {
				this.initializeTenantInfo();
				this.cdr.detectChanges();
			}
		});

		// First try to get the current user synchronously from localStorage
		const storedUser = localStorage.getItem('user');
		if (storedUser) {
			const user = JSON.parse(storedUser);
			this.applyUserDisplayState(user);

			// Subscribe to profile data from HomeStateService
			if (user?.credentials?.EmployeeNumber) {
				// OPTIMIZED: Profile settings with deep comparison
				this.profileSubscription =
					this.homeStateService.profileSettings$
						.pipe(
							filter((profile) => !!profile),
							distinctUntilChanged((prev, curr) => {
								// Deep comparison for profile changes
								return (
									JSON.stringify(prev) ===
									JSON.stringify(curr)
								);
							})
						)
						.subscribe((profile) => {
							this.updateProfileData(profile);
							this.cdr.detectChanges();
						});

				// OPTIMIZED: Tailoring data with smart comparison
				this.homeStateService.tailoring$
					.pipe(
						filter((tailoring) => !!tailoring),
						distinctUntilChanged((prev, curr) => {
							// Only compare AllowChangePassword for instant updates
							return (
								prev?.AllowChangePassword ===
								curr?.AllowChangePassword
							);
						})
					)
					.subscribe((tailoring) => {
						this.isUpdatePasswordAvailable =
							tailoring?.AllowChangePassword;
						this.cdr.detectChanges();
					});

				this.loadProfileSettingsIfNeeded(
					user.credentials.EmployeeNumber
				);
			}
		}

		// OPTIMIZED: User subscription with comprehensive property comparison
		this.userSubscription = this.authService.currentUser$
			.pipe(
				map((user: any) => {
					if (!user) return null;

					const mappedUser = {
						employeeNumber: user?.credentials?.EmployeeNumber,
						allowChangePassword:
							user?.tailoring?.AllowChangePassword,
						isTraining: user?.IsTraining,
						isTrainingAdmin: user?.IsTrainingAdmin,
						mailSettings: user?.MailSettings,
						trainingCourse: user?.TrainingCourse,
						courseNotifications: user?.CourseNotifications,
						trainingGroup: user?.TrainingGroup,
						employeeTraining: user?.EmployeeTraining,
						employeeNotifications: user?.EmployeeNotifications,
						isTimeCardLock: user?.IsTimeCardLock,
						timeClock: user?.tailoring?.TimeClock,
						inventory: user?.tailoring?.Inventory,
						isItemMaster: user?.IsItemMaster,
						isPurchaseOrder: user?.IsPurchaseOrder,
						isInventory: user?.IsInventory,
						isPTOIntegration: user?.IsPTO,
						isSupervisor: user?.isSupervisor,
						isReviewer: user?.canReviewFirstLevel,
						applicationUserName: user?.ApplicationUserName,
						// notificationsShown: user?.notificationsShown,
						forcePasswordReset: user?.forcePasswordReset,
						profile: user?.profile,
						fullUser: user
					};

					return mappedUser;
				}),
				distinctUntilChanged((prev, curr) => {
					// Allow all updates if either is null/undefined
					if (!prev || !curr) return false;

					// Smart comparison of only relevant properties
					return (
						prev.employeeNumber === curr.employeeNumber &&
						prev.allowChangePassword === curr.allowChangePassword &&
						prev.isTraining === curr.isTraining &&
						prev.isTrainingAdmin === curr.isTrainingAdmin &&
						prev.mailSettings === curr.mailSettings &&
						prev.trainingCourse === curr.trainingCourse &&
						prev.courseNotifications === curr.courseNotifications &&
						prev.trainingGroup === curr.trainingGroup &&
						prev.employeeTraining === curr.employeeTraining &&
						prev.employeeNotifications ===
							curr.employeeNotifications &&
						prev.isTimeCardLock === curr.isTimeCardLock &&
						prev.timeClock === curr.timeClock &&
						prev.inventory === curr.inventory &&
						prev.isItemMaster === curr.isItemMaster &&
						prev.isPurchaseOrder === curr.isPurchaseOrder &&
						prev.isInventory === curr.isInventory &&
						prev.isPTOIntegration === curr.isPTOIntegration &&
						prev.isSupervisor === curr.isSupervisor &&
						prev.isReviewer === curr.isReviewer &&
						prev.applicationUserName === curr.applicationUserName &&
						prev.forcePasswordReset === curr.forcePasswordReset &&
						JSON.stringify(prev.profile) ===
							JSON.stringify(curr.profile)
					);
				})
			)
			.subscribe((data) => {
				if (!data) {
					this.resetUserData();
					this.cdr.detectChanges();
					return;
				}

				this.applyUserDisplayState(data.fullUser);
				this.loadSidebarAccessDataIfNeeded(data.fullUser);

				// Force immediate change detection for instant UI updates
				this.cdr.detectChanges();

				// Only refresh profile data when user changes
				if (
					data.employeeNumber &&
					(!this.userData ||
						this.userData.credentials?.EmployeeNumber !==
							data.employeeNumber)
				) {
					this.loadProfileSettingsIfNeeded(data.employeeNumber);
				}
			});

		// Listen to logout events to clear data
		this.authService.logoutEvent.subscribe(() => {
			this.poService.invalidatePOUpdateFlagCache();
			this.resetUserData();
			this.cdr.detectChanges();
		});

		let currentTenant = localStorage.getItem('tenant');
		if (currentTenant !== null) {
			this.tenant = JSON.parse(currentTenant);
		}

		this.eventsSubscription = this.sharedService.events$.subscribe(
			(events) => {
				this.hasEvents = events && events.length > 0;
			}
		);

		this.notificationSubscription = this.notificationService
			.getNotifications()
			.subscribe((notifications) => {
				this.notifications = notifications || [];
			});

		if (this.forcePasswordReset) {
			let dialogRef = this.dialogRef.open(ChangePasswordComponent, {
				disableClose: true,
				backdropClass: 'custom-dialog-overlay',
				data: {
					forcePassword: true
				}
			});

			dialogRef.afterClosed().subscribe((result) => {
				if (result === 'logout') {
					this.onLogout();
				}
			});
		}
	}

	private loadProfileSettingsIfNeeded(
		employeeNumber: number,
		forceRefresh = false
	): void {
		if (!employeeNumber) {
			return;
		}

		if (
			!forceRefresh &&
			this.homeStateService.hasProfileAndTailoringCached(employeeNumber)
		) {
			this.homeStateService.emitCachedProfileAndTailoring(employeeNumber);
			return;
		}

		this.homeStateService
			.refreshProfileData(employeeNumber, forceRefresh)
			.subscribe();
	}

	private applyUserDisplayState(user: any): void {
		try {
			this.userData = user;
			this.resources = Array.isArray(user?.Resources) ? user.Resources : [];
			this.employeeNumber = user?.credentials?.EmployeeNumber;
			this.isSupervisor = user?.isSupervisor;
			this.isReviewer = user?.canReviewFirstLevel;
			this.IsTraining = user?.IsTraining;
			this.IsTrainingAdmin = user?.IsTrainingAdmin;
			this.MailSettings = user?.MailSettings;
			this.TrainingCourse = user?.TrainingCourse;
			this.CourseNotifications = user?.CourseNotifications;
			this.TrainingGroup = user?.TrainingGroup;
			this.EmployeeTraining = user?.EmployeeTraining;
			this.EmployeeNotifications = user?.EmployeeNotifications;
			// this.notificationsShown =
			// 	user?.notificationsShown || !user?.ApplicationUserName;
			this.forcePasswordReset = user?.forcePasswordReset;
			this.isItemMasterAccess = user?.IsItemMaster;
			this.isPurchaseOrderAccess = user?.IsPurchaseOrder;
			this.isInventoryAccess = user?.IsInventory;
			this.isPTOIntegration = user?.IsPTO;
			this.isUpdatePasswordAvailable =
				user?.tailoring?.AllowChangePassword;

			// Update profile data for IT access
			let profileData =
				user?.profile?.length > 0 && !Array.isArray(user?.profile)
					? JSON.parse(user?.profile)
					: user?.profile;

			if (profileData && Array.isArray(profileData)) {
				this.isIT = false;
				const hasITAccess = profileData.some(
					(itm: any) =>
						itm.ProfileTypeID == 5 &&
						(itm.SettingValue === 'true' ||
							itm.SettingValue === true)
				);

				if (hasITAccess) {
					this.isIT = true;
				}
			}

			this.UserName = user?.ApplicationUserName || '';

			this.NotificationPagingInfo = {
				...this.NotificationPagingInfo,
				EmployeeNumber: this.employeeNumber
			};
		} catch (error) {
			this.resetUserData();
		}
	}

	private loadSidebarAccessDataIfNeeded(user: any): void {
		if (!this.employeeNumber) {
			return;
		}

		if (this.sidebarAccessLoadedForEmployee === this.employeeNumber) {
			return;
		}

		const hasInventoryData =
			user?.IsInventoryAccess !== undefined &&
			user?.ApplicationUserName !== undefined;

		if (
			!hasInventoryData &&
			(!this.inventoryAccessChecked ||
				this.lastCheckedEmployeeNumber !== this.employeeNumber)
		) {
			this.checkInventoryAccess();
		} else {
			this.isInventoryAccess = user?.IsInventory ?? false;
			this.inventoryAccessChecked = true;
			this.lastCheckedEmployeeNumber = this.employeeNumber;
		}

		if (this.UserName) {
			this.getPOUpdateFlag();
		}

		this.sidebarAccessLoadedForEmployee = this.employeeNumber;
	}

	private updateProfileData(profileData: any): void {
		try {
			this.isIT = false;

			if (Array.isArray(profileData)) {
				const hasITAccess = profileData.some(
					(itm: any) =>
						itm.ProfileTypeID == 5 &&
						(itm.SettingValue === 'true' ||
							itm.SettingValue === true)
				);

				if (hasITAccess) {
					this.isIT = true;
				}
			}

			// Force immediate UI update
			this.cdr.detectChanges();
		} catch (error) {
			console.error('Error updating profile data:', error);
		}
	}

	private resetUserData(): void {
		this.resources = [];
		this.employeeNumber = 0;
		this.UserName = '';
		this.isSupervisor = false;
		this.isReviewer = false;
		this.IsTraining = false;
		this.IsTrainingAdmin = false;
		this.MailSettings = false;
		this.TrainingCourse = false;
		this.CourseNotifications = false;
		this.TrainingGroup = false;
		this.EmployeeTraining = false;
		this.EmployeeNotifications = false;
		this.isUpdatePasswordAvailable = false;
		this.forcePasswordReset = false;
		this.isIT = false;
		this.isInventoryAccess = false;

		// Reset inventory access tracking flags
		this.inventoryAccessChecked = false;
		this.lastCheckedEmployeeNumber = 0;
		this.inventoryAccessInFlight = false;
		this.sidebarAccessLoadedForEmployee = null;
		this.poUpdateFlagLoadedForUser = null;
		this.poUpdateFlagInFlight = false;
	}

	checkInventoryAccess(): void {
		if (!this.employeeNumber || this.inventoryAccessInFlight) {
			return;
		}

		if (
			this.inventoryAccessChecked &&
			this.lastCheckedEmployeeNumber === this.employeeNumber
		) {
			return;
		}

		if (this.userData?.IsInventory !== undefined) {
			this.isInventoryAccess = this.userData.IsInventory;
			this.cdr.detectChanges();
		}

		this.inventoryAccessInFlight = true;

		this.authService.checkInventoryAccess(this.employeeNumber).subscribe({
			next: (response: any) => {
				this.isInventoryAccess = response.IsInventoryAccess;
				this.inventoryAccessChecked = true;
				this.lastCheckedEmployeeNumber = this.employeeNumber;

				const updatedUserData = {
					...this.userData,
					ApplicationUserName: response.LienceUserName,
					IsInventoryAccess: response.IsInventoryAccess,
					IsInventory: response.IsInventoryAccess,
					IsPCSIAdmin: response.IsPCSIAdmin,
					IsSystemAdmin: response.IsSystemAdmin,
					IsPurchaseOrderAccess: response.IsPurchaseOrderAccess,
					EmployeeNumber: response.EmployeeNumber
				};

				const userNameChanged =
					this.userData?.ApplicationUserName !==
					response.LienceUserName;
				const inventoryChanged =
					this.userData?.IsInventoryAccess !==
					response.IsInventoryAccess;

				if (userNameChanged || inventoryChanged) {
					this.userData = updatedUserData;
					this.UserName = response.LienceUserName || '';
					this.authService.setCurrentUser(updatedUserData);
					if (
						this.UserName &&
						this.poUpdateFlagLoadedForUser !== this.UserName
					) {
						this.getPOUpdateFlag();
					}
				}

				this.cdr.detectChanges();
			},
			error: () => {
				this.inventoryAccessChecked = true;
				this.lastCheckedEmployeeNumber = this.employeeNumber;
				this.cdr.detectChanges();
			},
			complete: () => {
				this.inventoryAccessInFlight = false;
			}
		});
	}

	getImageUrl(image: any): string {
		return image.Image === this.constantService.DEFAULT_LOGO
			? this.constantService.DEFAULT_LOGO
			: `${this.constantService.IMAGE_URL}/${image.Image}`;
	}

	// Method to handle image loading errors
	handleImageError(image: any) {
		this.tenantLogo = this.constantService.DEFAULT_LOGO;
		this.cdr.detectChanges();
	}

	get sidebarOpen(): boolean {
		return this._sidebarOpen;
	}

	set sidebarOpen(value: boolean) {
		this._sidebarOpen = value;
	}

	get submenuOpen(): boolean {
		return this._submenuOpen;
	}

	set submenuOpen(value: boolean) {
		this._submenuOpen = value;
	}

	togglesidebar() {
		this.sidebarOpen = !this.sidebarOpen;
	}

	settingOpen: boolean = false;

	toggleSetting() {
		this.settingOpen = !this.settingOpen;
	}

	submenuStates: { [key: string]: boolean } = {
		training: false,
		'item-master': false,
		'inventory-master': false,
		'purchase-order': false,
		'pto-requests': false
	};

	toggleSubmenu(submenuKey: string) {
		Object.keys(this.submenuStates).forEach((key) => {
			if (key !== submenuKey) {
				this.submenuStates[key] = false;
			}
		});
		this.submenuStates[submenuKey] = !this.submenuStates[submenuKey];
	}

	goToPTORequests(event: Event): void {
		event.preventDefault();
		this.router.navigate([`/${this.rcService.getTenant()}/pto-requests`]);
	}

	isActive(): boolean {
		const url = this.router.url;
		const tenant = this.rcService.getTenant();
		return (
			url === `/${tenant}` ||
			url === `/${tenant}/home` ||
			url === `/${tenant}/details/categories` ||
			url === '/'
		);
	}

	isDetailsPage(): boolean {
		return this.router.url.includes('/details/');
	}

	/**
	 * Resource links are external (DocuSign and the like), so they open in a popup rather than
	 * routing inside the app.
	 */
	openResource(resourcePath: string): void {
		if (!resourcePath) {
			return;
		}

		window.open(
			resourcePath,
			'_blank',
			'width=800,height=600,toolbar=yes,location=yes,menubar=yes,scrollbars=yes,resizable=yes'
		);
	}

	onLogout() {
		this.router.navigate([`/${this.rcService.getTenant()}/auth/login`]);
	}

	//Open training settings dialog
	openTrainingSettingDailog() {
		this.dialogRef.open(SettingsComponent, {
			width: '550px', // Set the desired width
			panelClass: 'custom-training-setting'
		});
	}

	openChangePasswordDailog() {
		this.dialogRef.open(ChangePasswordComponent, {
			data: {
				forcePassword: false
			}
		});
	}

	openProfileDailog() {
		const dialogRef = this.dialogRef.open(ProfileComponent, {
			width: '750px',
			panelClass: 'custom-profile-setting',
			data: {}
		});

		// Subscribe to dialog close event to trigger instant user data refresh
		dialogRef.afterClosed().subscribe(() => {
			// Force immediate user data refresh when profile dialog closes
			this.authService.forceEmitCurrentUser();

			// Multiple change detection cycles for instant updates
			this.cdr.detectChanges();

			setTimeout(() => {
				this.cdr.detectChanges();
			}, 10);

			setTimeout(() => {
				this.cdr.detectChanges();
			}, 100);
		});
	}

	openITPasswordTool() {
		this.dialogRef.open(ItPasswordResetComponent);
	}

	handleSidebarClick(event: Event): void {
		const clickedElement = event.target as HTMLElement;
		const isSubmenuToggleClicked =
			clickedElement.closest('.submenu-toggle') !== null;
		const isSubmenuLinkClicked =
			clickedElement.closest('.submenu-container') !== null;
		const isAnchorOrChildClicked = clickedElement.closest('a') !== null;
		if (isSubmenuLinkClicked) {
			// Close the sidebar if clicking on any links within the sub-menu
			this.sidebarOpen = false;
		} else if (
			isSubmenuToggleClicked === false &&
			isSubmenuLinkClicked === false
		) {
			Object.keys(this.submenuStates).forEach((key) => {
				this.submenuStates[key] = false;
			});
			this.sidebarOpen = false;
		} else {
			// Close the sidebar only if clicking outside the sub-menu container or on the sub-menu toggle
			if (!isSubmenuToggleClicked) {
				this.sidebarOpen = false;
			}
		}
	}

	@HostListener('document:click', ['$event'])
	handleClickOutside(event: Event) {
		const clickedInside = this.el.nativeElement.contains(event.target);
		if (!clickedInside && this.sidebarOpen) {
			this.sidebarOpen = false;
		}
	}

	getTrainingNotificationsMessageByEmployeeNumber() {
		this.trainingService
			.getTrainingNotificationsMessageByEmployeeNumber(
				this.NotificationPagingInfo
			)
			.subscribe((d: any) => {
				if (d.data.length > 0) {
					this.trainingNotificationList = d.data;
					this.NotificationPagingInfo.totalItems =
						d.data[0]['TotalCount'];
				}
			});
	}

	getPOUpdateFlag(forceRefresh = false) {
		if (!this.UserName) {
			return;
		}

		if (this.poUpdateFlagInFlight) {
			return;
		}

		if (!forceRefresh && this.poUpdateFlagLoadedForUser === this.UserName) {
			return;
		}

		this.poUpdateFlagInFlight = true;

		this.poService.getPOUpdateFlag(this.UserName, forceRefresh).subscribe({
			next: (response) => {
				this.isDisabled = !response.POUpdate_ProcessState_UpdateEnabled;
				this.poUpdateFlagLoadedForUser = this.UserName;
				this.poUpdateFlagInFlight = false;
				this.cdr.detectChanges();
			},
			error: () => {
				this.isDisabled = true;
				this.poUpdateFlagInFlight = false;
				this.cdr.detectChanges();
			}
		});
	}

	handlePOUpdateRequest(event?: Event): void {
		if (!this.checkApplicationUserName(event)) {
			return;
		}

		if (!this.UserName) {
			this.toastr.error('User information not available', 'Error', {
				positionClass: 'toast-bottom-left',
				toastClass: 'toast-icon custom-toast-error'
			});
			return;
		}

		this.alertService
			.showConfirm(
				'Are you sure?',
				'You want to run PO Update?',
				'warning'
			)
			.then((confirm) => {
				if (confirm) {
					this.poService.POUpdate(this.UserName).subscribe({
						next: (response) => {
							if (response.Result) {
								this.toastr.success(
									response.Message,
									'Success',
									{
										positionClass: 'toast-bottom-left',
										toastClass:
											'toast-icon custom-toast-success'
									}
								);
								this.getPOUpdateFlag(true);
							} else {
								this.toastr.error(response.Message, 'Error', {
									positionClass: 'toast-bottom-left',
									toastClass: 'toast-icon custom-toast-error'
								});
							}
						},
						error: (error) => {
							this.toastr.error(
								'Something went wrong, please try again',
								'Error',
								{
									positionClass: 'toast-bottom-left',
									toastClass: 'toast-icon custom-toast-error'
								}
							);
						}
					});
				}
			});
	}

	/**
	 * Global function to check if ApplicationUserName exists before navigation
	 * @param event - The click event to prevent default behavior
	 * @param callback - Optional callback function to execute if validation passes
	 * @returns boolean - true if validation passes, false otherwise
	 */
	checkApplicationUserName(event?: Event, callback?: () => void): boolean {
		if (!this.userData?.ApplicationUserName) {
			// Prevent the default navigation
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}

			this.toastr.error(
				'USERNAME absent - In the V5 software, on the HR > Employee tab - Please update the USERNAME field in the Employee tab (bottom of the screen).',
				'USERNAME absent',
				{
					positionClass: 'toast-bottom-left',
					toastClass: 'toast-icon custom-toast-error'
				}
			);
			return false;
		}

		// If validation passes and callback provided, execute it
		if (callback) {
			callback();
		}

		return true;
	}

	/**
	 * Helper method to check permission with ApplicationUserName validation
	 * For Inventory, Item Master, and Purchase Order menus
	 * Permissions are only evaluated if ApplicationUserName check passes
	 * If ApplicationUserName is missing, menus appear enabled (not disabled)
	 * @param permission - The permission string to check
	 * @returns boolean - true if ApplicationUserName is missing OR (ApplicationUserName exists AND permission is granted)
	 */
	hasPermissionWithUsernameCheck(permission: string): boolean {
		// If ApplicationUserName is missing, show menus as enabled (don't disable them)
		// navigateWithCheck will handle showing the error message when clicked
		if (!this.userData?.ApplicationUserName) {
			return true; // Show as enabled, even without ApplicationUserName
		}
		// Only check permissions if ApplicationUserName exists
		return this.permissions().has(permission);
	}

	get isBulkMaterialActionDisabled(): boolean {
		const p = this.permissions();

		return (
			!p.has('btnICBulkProcess.cmdBuild') &&
			!p.has('btnICBulkProcess.cmdEntry') &&
			!p.has('btnICBulkProcess.cmdReport') &&
			!p.has('btnICBulkProcess.cmdUpdate') &&
			!p.has('btnICBulkRelease.cmdEntry') &&
			!p.has('btnICBulkRelease.cmdReport') &&
			!p.has('btnICBulkBatch.cmdEntry') &&
			!p.has('btnICBulkBatch.cmdReport') &&
			!p.has('btnICBulkBatch.cmdUpdate')
		);
	}

	get isMaterialRequisitionActionDisabled(): boolean {
		const p = this.permissions();

		return (
			!p.has('btnICRequisitionProcess.cmdBuild') &&
			!p.has('btnICRequisitionProcess.cmdEntry') &&
			!p.has('btnICRequisitionProcess.cmdReport') &&
			!p.has('btnICRequisitionProcess.cmdUpdate') &&
			!p.has('btnICRequisitionRelease.cmdEntry') &&
			!p.has('btnICRequisitionRelease.cmdReport') &&
			!p.has('btnICRequisitionBatch.cmdEntry') &&
			!p.has('btnICRequisitionBatch.cmdReport') &&
			!p.has('btnICRequisitionBatch.cmdUpdate')
		);
	}

	get isTransferMaintenanceActionDisabled(): boolean {
		const p = this.permissions();

		return (
			!p.has('btnICTransferProcess.cmdBuild') &&
			!p.has('btnICTransferProcess.cmdEntry') &&
			!p.has('btnICTransferProcess.cmdReport') &&
			!p.has('btnICTransferProcess.cmdUpdate') &&
			!p.has('btnICTransferRelease.cmdEntry') &&
			!p.has('btnICTransferRelease.cmdReport') &&
			!p.has('btnICTransferBatch.cmdEntry') &&
			!p.has('btnICTransferBatch.cmdReport') &&
			!p.has('btnICTransferBatch.cmdUpdate')
		);
	}

	get isFinishGoodsActionDisabled(): boolean {
		const p = this.permissions();

		return (
			!p.has('btnICFinishedGoods.cmdEntry') &&
			!p.has('btnICFinishedGoods.cmdReport') &&
			!p.has('btnICFinishedGoods.cmdUpdate')
		);
	}

	get isPhysicalInventoryActionDisabled(): boolean {
		const p = this.permissions();

		return (
			!p.has('btnICPhysicalInventory.cmdEntry') &&
			!p.has('btnICPhysicalInventory.cmdReport') &&
			!p.has('btnICPhysicalInventory.cmdUpdate')
		);
	}

	get isStockOrderMaintenanceActionDisabled(): boolean {
		const p = this.permissions();

		return (
			!p.has('pcsbtnICStockOrderMtce') &&
			!p.has('pcsProcessStockOrderPick.cmdEntry') &&
			!p.has('pcsProcessStockOrderPick.cmdReport') &&
			!p.has('pcsProcessStockOrderProcess.cmdEntry') &&
			!p.has('pcsProcessStockOrderProcess.cmdReport') &&
			!p.has('pcsProcessStockOrderProcess.cmdUpdate')
		);
	}

	get isInventoryAdjustmentActionDisabled(): boolean {
		const p = this.permissions();

		return (
			!p.has('pcspICAdjustment.cmdEntry') &&
			!p.has('pcspICAdjustment.cmdReport') &&
			!p.has('pcspICAdjustment.cmdUpdate')
		);
	}

	get isStockReceiptsActionDisabled(): boolean {
		const p = this.permissions();

		return (
			!p.has('pPOStockReceiptsUpdate.cmdEntry') &&
			!p.has('pPOStockReceiptsUpdate.cmdReport') &&
			!p.has('pPOStockReceiptsUpdate.cmdUpdate')
		);
	}

	get hasICMaintenancePermission(): boolean {
		const p = this.icPermissions();
		return (
			p.has('FileMaintenance/frmICAccountCrossReferenceMaintenance') &&
			p.has('FileMaintenance/frmICBuyerMaster') &&
			p.has('FileMaintenance/frmICInventoryTypeMaintenance') &&
			p.has('FileMaintenance/frmICDepartmentMaintenance') &&
			p.has('FileMaintenance/frmICModuleControlMaintenance') &&
			p.has('FileMaintenance/frmICUnitOfMeasureMaintenance')
		);
	}

	get hasICYearEndPermission(): boolean {
		const p = this.icPermissions();

		return (
			p.has('YearEnd/frmICInventoryClearUsageQuantities') &&
			p.has('YearEnd/ICPurgeExceptionReport') &&
			p.has('YearEnd/ICPurgeMaster') &&
			p.has('YearEnd/frmICPurgeStockReceiptsHistory') &&
			p.has('YearEnd/frmICPurgeMaterialUsage')
		);
	}

	get isPOReqMaintenanceActionDisabled(): boolean {
		const p = this.poPermissions();

		return (
			!p.has('pcsbtnPOReqMaintenance') &&
			!p.has('pcsRequisitionProcess.cmdEntry') &&
			!p.has('pcsRequisitionProcess.cmdReport') &&
			!p.has('pcsRequisitionProcess.cmdUpdate')
		);
	}

	get hasPOMaintenancePermission(): boolean {
		const p = this.poPermissions();
		return (
			p.has('FileMaintenance/frmGBEmpSignature') &&
			p.has('FileMaintenance/frmPOModuleControlMaintenance') &&
			p.has('FileMaintenance/frmPOClass') &&
			p.has('FileMaintenance/frmPOVendor') &&
			p.has('FileMaintenance/frmPOShipToMaintenance') &&
			p.has('FileMaintenance/frmICUnitOfMeasureMaintenance')
		);
	}

	get hasPOYearEndPermission(): boolean {
		const p = this.poPermissions();

		return (
			p.has('YearEnd/frmPOPurgeExceptionReport') &&
			p.has('YearEnd/frmPOPurge') &&
			p.has('YearEnd/frmPOPurge_Audit')
		);
	}

	/**
	 * Wrapper for navigation with username check
	 * @param event - The click event
	 * @param path - The navigation path (can be a string with query params or an array)
	 */
	navigateWithCheck(event: Event, path: string | any[]): void {
		if (this.checkApplicationUserName(event)) {
			if (Array.isArray(path)) {
				// If it's already an array, check if any element contains query params
				const processedPath = path.map((p) => {
					if (typeof p === 'string' && p.includes('?')) {
						// Extract path without query params
						return p.split('?')[0];
					}
					return p;
				});

				// Extract query params from all path elements
				const allQueryParams: { [key: string]: string } = {};
				path.forEach((p) => {
					if (typeof p === 'string' && p.includes('?')) {
						const queryString = p.split('?')[1];
						const params = new URLSearchParams(queryString);
						params.forEach((value, key) => {
							allQueryParams[key] = value;
						});
					}
				});

				// Navigate with query params if any were found
				if (Object.keys(allQueryParams).length > 0) {
					this.router.navigate(processedPath, {
						queryParams: allQueryParams
					});
				} else {
					this.router.navigate(processedPath);
				}
			} else {
				// If it's a string, check if it contains query params
				if (path.includes('?')) {
					const [urlPath, queryString] = path.split('?');
					const queryParams: { [key: string]: string } = {};
					const params = new URLSearchParams(queryString);
					params.forEach((value, key) => {
						queryParams[key] = value;
					});
					this.router.navigate([urlPath], { queryParams });
				} else {
					this.router.navigate([path]);
				}
			}
		}
	}

	/**
	 * Wrapper for dialog opening with username check
	 * @param event - The click event
	 * @param dialogComponent - The dialog component to open
	 * @param config - Optional dialog configuration
	 */
	openDialogWithCheck(
		event: Event,
		dialogComponent: any,
		config?: any
	): void {
		if (this.checkApplicationUserName(event)) {
			this.dialogRef.open(dialogComponent, config);
		}
	}

	// Update existing navigation methods to use the check

	goToItemMaster(event: Event): void {
		this.navigateWithCheck(event, [
			`/${this.rcService.getTenant()}/inventory/maintenance`
		]);
	}

	goToInventoryMaster(event: Event): void {
		this.navigateWithCheck(event, [
			`/${this.rcService.getTenant()}/inventory/master`
		]);
	}

	goToPurchaseOrder(event: Event): void {
		this.navigateWithCheck(event, [
			`/${this.rcService.getTenant()}/purchase-orders`
		]);
	}

	ngOnDestroy(): void {
		if (this.eventsSubscription) {
			this.eventsSubscription.unsubscribe();
		}
		if (this.profileSubscription) {
			this.profileSubscription.unsubscribe();
		}
		if (this.userSubscription) {
			this.userSubscription.unsubscribe();
		}
		if (this.notificationSubscription) {
			this.notificationSubscription.unsubscribe();
		}
		if (this.rcSubscription) {
			this.rcSubscription.unsubscribe();
		}
		this.poSubscription.unsubscribe();
	}
}
